/**
 * Hook for transit search, cache management, and loading state.
 */
import { useState, useCallback } from 'react';
import { apiService } from '../../../services/apiService';

export const useTransitSearch = ({ observatoryLocation, filters, onSavePreferences }) => {
  const [transits, setTransits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState(null);

  const checkCacheStatus = useCallback(async () => {
    try {
      const response = await apiService.getExoplanetCacheStatus();
      const prev = cacheStatus;
      setCacheStatus(response.data);

      if (response.data.is_refreshing) {
        setTimeout(checkCacheStatus, 5000);
      } else if (prev?.is_refreshing && !response.data.is_refreshing && response.data.total_exoplanets > 0) {
        setError({ type: 'info', message: `Cache refresh complete. ${response.data.total_exoplanets} exoplanets loaded.` });
      }
    } catch (err) {
      console.error('Failed to check cache status:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCache = useCallback(async () => {
    try {
      setError(null);
      await apiService.updateExoplanetCache();
      setError({ type: 'info', message: 'Cache update started in background. This may take a few minutes.' });
      setTimeout(checkCacheStatus, 10000);
    } catch (err) {
      console.error('Failed to update cache:', err);
      setError({ type: 'error', message: 'Failed to update cache' });
    }
  }, [checkCacheStatus]);

  const clearAndRefreshCache = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const response = await apiService.clearAndRefreshExoplanetCache();
      setError({ type: 'info', message: response.message || 'Cache clear and refresh started. This may take 1-2 minutes.' });
      setTransits([]);
      setTimeout(() => {
        checkCacheStatus();
        setLoading(false);
      }, 15000);
    } catch (err) {
      console.error('Failed to clear and refresh cache:', err);
      setError({ type: 'error', message: 'Failed to clear and refresh cache' });
      setLoading(false);
    }
  }, [checkCacheStatus]);

  const searchTransits = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        hours: filters.hours,
        min_depth: filters.minDepth,
        max_vmag: filters.maxVMag,
        min_altitude: filters.minAltitude,
      };

      if (observatoryLocation) {
        params.latitude = observatoryLocation.latitude;
        params.longitude = observatoryLocation.longitude;
      }

      if (filters.enableMoonFilter) {
        params.moon_peak_separation = filters.moonPeakSeparation;
        params.moon_lorentzian_width = filters.moonLorentzianWidth;
      }

      const response = await apiService.getUpcomingTransits(params);
      setTransits(response.data.transits || []);

      if (response.data.transits?.length === 0) {
        setError({ type: 'info', message: 'No transits found matching your criteria. Try adjusting the filters.' });
      }

      if (onSavePreferences) onSavePreferences();
    } catch (err) {
      console.error('Failed to search transits:', err);
      const isTimeout = err.code === 'ECONNABORTED' || err.message?.includes('timeout');
      const message = isTimeout
        ? 'Transit search timed out -- the exoplanet cache may still be loading. Please try again in a minute.'
        : `Failed to search for transits: ${err.response?.data?.error?.message || err.message}`;
      setError({ type: 'error', message });
      setTransits([]);
    } finally {
      setLoading(false);
    }
  }, [filters, observatoryLocation, onSavePreferences]);

  return {
    transits,
    setTransits,
    loading,
    error,
    setError,
    cacheStatus,
    checkCacheStatus,
    updateCache,
    clearAndRefreshCache,
    searchTransits,
  };
};
